import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  CircularProgress,
  useMediaQuery,
  Chip,
  Stack,
  Divider,
  Tooltip as MuiTooltip,
  Skeleton,
  Button,
  Paper,
  Avatar,
  Badge,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { tokens } from "../../../theme";
import {
  Language,
  InfoOutlined,
  PieChart as PieChartIcon,
  GridOn,
  ErrorOutline,
  Map,
  ArrowUpward,
  ArrowDownward,
  MonetizationOn,
  ShoppingCart,
  Download,
  TableChart,
} from "@mui/icons-material";

const RegionSalesDistribution = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const token = localStorage.getItem("token");
  const cmpid = localStorage.getItem("cmpid");

  // State management
  const [data, setData] = useState({
    indianStates: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUsingSampleData, setIsUsingSampleData] = useState(false);
  const [viewMode, setViewMode] = useState("pie"); // 'pie', 'grid', or 'table'
  const [prevData, setPrevData] = useState(null);

  // Stats state
  const [stats, setStats] = useState({
    india: {
      totalSales: 0,
      topRegion: "",
      topRegionValue: 0,
      topRegionPercentage: 0,
      averagePerRegion: 0,
    },
  });

  // Configuration constants
  const CONFIG = {
    chart: {
      innerRadius: 60,
      outerRadius: 90,
      legendPosition: {
        vertical: "middle",
        horizontal: "right",
      },
    },
    colors: {
      // Enhanced aesthetic color palette for Indian states
      indiaColors: [
        "#FF6B6B", // Soft Coral
        "#4ECDC4", // Tiffany Blue
        "#45B7D1", // Sky Blue
        "#A37EBA", // Soft Purple
        "#0e4eb1", // Rose Quartz
        "#92A8D1", // Serenity Blue
        "#88B04B", // Greenery
        "#E15D44", // Spicy Orange
        "#5B5EA6", // Ultra Violet
        "#DD4124", // Classic Red
        "#D65076", // Rose Red
        "#009B77", // Tropical Green
        "#B565A7", // Orchid Bloom
        "#955251", // Marsala
        "#EFC050", // Mimosa
      ],
      highlight: colors.blueAccent[700],
      tooltipBg: theme.palette.background.paper,
      cardBg: theme.palette.background.default,
      success: colors.greenAccent[500],
      warning: colors.redAccent[500],
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
    },
  };

  // Generate sample data for demo purposes
  const generateSampleData = () => {
    const indianStates = [
      { name: "Maharashtra", revenue: 18000, orders: 450 },
      { name: "Delhi", revenue: 15000, orders: 380 },
      { name: "Karnataka", revenue: 12000, orders: 320 },
      { name: "Tamil Nadu", revenue: 11000, orders: 290 },
      { name: "Uttar Pradesh", revenue: 10000, orders: 270 },
      { name: "Gujarat", revenue: 9000, orders: 240 },
      { name: "West Bengal", revenue: 8000, orders: 210 },
      { name: "Rajasthan", revenue: 7000, orders: 190 },
      { name: "Andhra Pradesh", revenue: 6500, orders: 170 },
      { name: "Telangana", revenue: 6000, orders: 160 },
    ];

    return { indianStates };
  };

  // Calculate statistics from data
  const calculateStats = (data) => {
    const calculate = (dataArray) => {
      if (dataArray.length === 0) return {};

      const totalSales = dataArray.reduce((sum, item) => sum + item.revenue, 0);
      const totalOrders = dataArray.reduce((sum, item) => sum + item.orders, 0);
      const topRegion = dataArray.reduce(
        (max, item) => (item.revenue > max.revenue ? item : max),
        dataArray[0]
      );

      return {
        totalSales,
        totalOrders,
        topRegion: topRegion.name,
        topRegionValue: topRegion.revenue,
        topRegionPercentage: Math.round((topRegion.revenue / totalSales) * 100),
        averagePerRegion: Math.round(totalSales / dataArray.length),
        averageOrdersPerRegion: Math.round(totalOrders / dataArray.length),
      };
    };

    // Filter out empty state names before calculating stats
    const filteredStates = data.indianStates.filter(
      (state) => state.name && state.name.trim() !== ""
    );

    return {
      india: calculate(filteredStates),
    };
  };

  // Calculate change percentage
  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  // Check if API data is empty
  const isDataEmpty = (apiData) => {
    return (
      !apiData ||
      !apiData.indianStates ||
      apiData.indianStates.length === 0 ||
      apiData.indianStates.every(
        (state) => !state.name || state.name.trim() === ""
      )
    );
  };

  // Fetch data from API
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      setIsUsingSampleData(false);

      // Store current data as previous before fetching new data
      setPrevData(data);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/reports/regionWiseSalesDistribution`,
        { cmpid },
        { headers: { Authorization: token } }
      );

      // Check if API data is empty
      if (isDataEmpty(response.data)) {
        setError("No data available. Showing sample data.");
        const sampleData = generateSampleData();
        setData(sampleData);
        setStats(calculateStats(sampleData));
        setIsUsingSampleData(true);
      } else {
        // Filter out empty state names from the response
        const filteredStates = response.data.indianStates.filter(
          (state) => state.name && state.name.trim() !== ""
        );

        setData({ indianStates: filteredStates || [] });
        setStats(calculateStats({ indianStates: filteredStates }));
      }
    } catch (err) {
      console.error("Error fetching region sales data:", err);
      setError("No data available. Showing sample data.");
      const sampleData = generateSampleData();
      setData(sampleData);
      setStats(calculateStats(sampleData));
      setIsUsingSampleData(true);
    } finally {
      setLoading(false);
    }
  };

  // Toggle between views
  const toggleViewMode = () => {
    const views = ["pie", "grid", "table"];
    const currentIndex = views.indexOf(viewMode);
    setViewMode(views[(currentIndex + 1) % views.length]);
  };

  // Export data to CSV
  const exportToCSV = () => {
    // Filter out empty state names before exporting
    const activeData = data.indianStates.filter(
      (state) => state.name && state.name.trim() !== ""
    );
    const headers = ["Region", "Revenue", "Orders", "Percentage"];

    const rows = activeData.map((region) => {
      const total = stats.india.totalSales;
      const percentage = Math.round((region.revenue / total) * 100);

      return [
        region.name,
        `Rs.${region.revenue}`,
        region.orders,
        `${percentage}%`,
      ];
    });

    // Add summary row
    rows.push([
      "TOTAL",
      `Rs.${stats.india.totalSales}`,
      stats.india.totalOrders,
      "100%",
    ]);

    let csvContent =
      "data:text/csv;charset=utf-8," +
      headers.join(",") +
      "\n" +
      rows.map((row) => row.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `indian_states_sales.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dataItem = payload[0].payload;
      const total = stats.india.totalSales;
      const percentage = Math.round((payload[0].value / total) * 100);

      return (
        <Card
          sx={{
            backgroundColor: CONFIG.colors.tooltipBg,
            boxShadow: theme.shadows[3],
            borderRadius: 2,
            p: 2,
            minWidth: 180,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {dataItem.name}
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Stack spacing={1}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center" gap={0.5}>
                <MonetizationOn fontSize="small" color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Revenue:
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600}>
                Rs.{dataItem.revenue}
              </Typography>
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center" gap={0.5}>
                <ShoppingCart fontSize="small" color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Orders:
                </Typography>
              </Box>
              <Typography variant="body2">{dataItem.orders}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Percentage:
              </Typography>
              <Typography variant="body2">{percentage}%</Typography>
            </Box>
          </Stack>
        </Card>
      );
    }
    return null;
  };

  // Custom legend formatter
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
    name,
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={12}
        fontWeight={600}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Effect to fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Calculate changes from previous data
  const totalSalesChange = prevData
    ? calculateChange(
        stats.india.totalSales,
        calculateStats(prevData).india.totalSales
      )
    : 0;

  // Filter out empty state names before rendering
  const filteredStates = data.indianStates.filter(
    (state) => state.name && state.name.trim() !== ""
  );

  return (
    <Card
      sx={{
        height: "100%",
        minHeight: 450, // Adjusted to match CustomerTypeRatio
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[2],
        borderRadius: "12px",
        overflow: "hidden",
        border: `1px solid ${theme.palette.divider}`,
        display: "flex",
        flexDirection: "column",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: theme.shadows[6],
        },
      }}
    >
      <CardContent
        sx={{
          p: isMobile ? 2 : 3,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Header Section */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: CONFIG.colors.textPrimary,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Avatar
                sx={{
                  bgcolor: colors.primary[500],
                  width: 32,
                  height: 32,
                  boxShadow: theme.shadows[2],
                }}
              >
                <Map fontSize="small" />
              </Avatar>
              Indian States Sales Distribution
              <MuiTooltip title="Sales distribution across Indian states">
                <InfoOutlined
                  sx={{
                    fontSize: 18,
                    color: CONFIG.colors.textSecondary,
                    cursor: "pointer",
                  }}
                />
              </MuiTooltip>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Geographic performance analysis by Indian states
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Button
              size="small"
              onClick={toggleViewMode}
              startIcon={
                viewMode === "pie" ? (
                  <GridOn />
                ) : viewMode === "grid" ? (
                  <TableChart />
                ) : (
                  <PieChartIcon />
                )
              }
              sx={{
                textTransform: "none",
                color: CONFIG.colors.textSecondary,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              {viewMode === "pie"
                ? "Grid"
                : viewMode === "grid"
                ? "Table"
                : "Chart"}
            </Button>

            <Button
              size="small"
              onClick={exportToCSV}
              startIcon={<Download />}
              sx={{
                textTransform: "none",
                color: CONFIG.colors.textSecondary,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              Export
            </Button>
          </Box>
        </Box>

        {/* Error/Sample Data Notice */}
        {error && (
          <Box>
            <Chip
              label={error}
              color="warning"
              size="small"
              variant="outlined"
              icon={<ErrorOutline fontSize="small" />}
              sx={{
                mb: 0.5,
                borderColor: colors.warning,
                color: colors.warning,
              }}
            />
            {isUsingSampleData && (
              <Typography variant="caption" color="text.secondary">
                Sample data may not reflect actual business metrics
              </Typography>
            )}
          </Box>
        )}

        {/* Stats Summary */}
        <Box display="flex" flexDirection="column" gap={2}>
          {loading ? (
            <Skeleton variant="rounded" width="100%" height={120} />
          ) : (
            <Grid container spacing={2}>
              {/* Total Sales Card */}
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  title="Total Sales"
                  value={`Rs.${stats.india.totalSales}`}
                  icon={<Map />}
                  theme={theme}
                  colors={colors}
                  change={totalSalesChange}
                />
              </Grid>

              {/* Top Region Card */}
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  title="Top State"
                  value={stats.india.topRegion}
                  description={`Rs.${stats.india.topRegionValue} (${stats.india.topRegionPercentage}%)`}
                  icon={<Language />}
                  theme={theme}
                  colors={colors}
                />
              </Grid>

              {/* Average Per Region Card */}
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  title="Avg per State"
                  value={`Rs.${stats.india.averagePerRegion}`}
                  description={`${filteredStates.length} states`}
                  theme={theme}
                  colors={colors}
                />
              </Grid>
            </Grid>
          )}
        </Box>

        {/* Chart Area */}
        <Box flex={1} minHeight={250} position="relative" mt={2}>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
              bgcolor={theme.palette.action.hover}
              borderRadius={2}
            >
              <CircularProgress size={isMobile ? 40 : 50} />
            </Box>
          ) : viewMode === "pie" ? (
            <Box
              height="100%"
              bgcolor={theme.palette.action.hover}
              borderRadius={2}
              p={2}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.action.hover} 100%)`,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={filteredStates}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    outerRadius={CONFIG.chart.outerRadius}
                    innerRadius={CONFIG.chart.innerRadius}
                    dataKey="revenue"
                    nameKey="name"
                    animationDuration={500}
                    animationEasing="ease-out"
                  >
                    {filteredStates.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          CONFIG.colors.indiaColors[
                            index % CONFIG.colors.indiaColors.length
                          ]
                        }
                        stroke={theme.palette.background.paper}
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
                  />
                  <Legend
                    layout={isMobile ? "horizontal" : "vertical"}
                    verticalAlign={isMobile ? "bottom" : "middle"}
                    align={isMobile ? "center" : "right"}
                    wrapperStyle={{
                      paddingLeft: isMobile ? 0 : "20px",
                      paddingTop: isMobile ? "10px" : 0,
                    }}
                    formatter={(value, entry, index) => {
                      const percentage = Math.round(
                        (filteredStates[index].revenue /
                          stats.india.totalSales) *
                          100
                      );
                      return `${value} (${percentage}%)`;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          ) : viewMode === "grid" ? (
            <Box
              display="grid"
              gridTemplateColumns={
                isMobile ? "1fr" : "repeat(auto-fill, minmax(220px, 1fr))"
              }
              gap={2}
              height="100%"
              overflow="auto"
              p={1}
              sx={{
                "&::-webkit-scrollbar": {
                  height: 6,
                  width: 6,
                },
                "&::-webkit-scrollbar-track": {
                  background: theme.palette.divider,
                  borderRadius: 10,
                },
                "&::-webkit-scrollbar-thumb": {
                  background: theme.palette.text.secondary,
                  borderRadius: 10,
                },
              }}
            >
              {filteredStates.map((region, index) => (
                <Paper
                  key={region.name}
                  elevation={3}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${
                      CONFIG.colors.indiaColors[
                        index % CONFIG.colors.indiaColors.length
                      ]
                    } 0%, ${
                      theme.palette.augmentColor({
                        color: {
                          main: CONFIG.colors.indiaColors[
                            index % CONFIG.colors.indiaColors.length
                          ],
                        },
                      }).dark
                    } 100%)`,
                    color: "white",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    minHeight: 100,
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: theme.shadows[6],
                    },
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      {region.name}
                    </Typography>
                    <Badge
                      badgeContent={index + 1}
                      color="primary"
                      sx={{
                        "& .MuiBadge-badge": {
                          right: -5,
                          top: -5,
                          backgroundColor: theme.palette.background.paper,
                          color: theme.palette.text.primary,
                          fontWeight: 700,
                        },
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Rs.{region.revenue}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {region.orders} orders
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>State</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                    <TableCell align="right">Orders</TableCell>
                    <TableCell align="right">Percentage</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredStates.map((state) => {
                    const percentage = Math.round(
                      (state.revenue / stats.india.totalSales) * 100
                    );
                    return (
                      <TableRow key={state.name}>
                        <TableCell component="th" scope="row">
                          {state.name}
                        </TableCell>
                        <TableCell align="right">Rs. {state.revenue}</TableCell>
                        <TableCell align="right">{state.orders}</TableCell>
                        <TableCell align="right">{percentage}%</TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ fontWeight: 700 }}
                    >
                      Total
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      Rs. {stats.india.totalSales}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {stats.india.totalOrders}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      100%
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// Reusable StatCard component
const StatCard = ({
  title,
  value,
  change,
  description,
  icon,
  theme,
  colors,
  isHighlight,
}) => {
  const isPositive = change >= 0;
  const changeColor = isPositive
    ? colors.greenAccent[500]
    : colors.redAccent[500];

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        p: 2,
        borderRadius: 2,
        backgroundColor: theme.palette.background.default,
        borderColor: isHighlight ? colors.primary[500] : theme.palette.divider,
        boxShadow: isHighlight ? `0 0 0 2px ${colors.primary[100]}` : "none",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: `0 0 0 1px ${
            isHighlight ? colors.primary[500] : theme.palette.divider
          }`,
          transform: "translateY(-2px)",
        },
      }}
    >
      {isHighlight && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: 4,
            height: "100%",
            backgroundColor: colors.primary[500],
          }}
        />
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        {icon &&
          React.cloneElement(icon, {
            fontSize: "small",
            sx: {
              color: isHighlight
                ? colors.primary[500]
                : theme.palette.text.secondary,
              opacity: 0.8,
            },
          })}
      </Box>

      <Typography variant="h5" fontWeight={700} mt={0.5}>
        {value}
      </Typography>

      {description && (
        <Typography variant="caption" color="text.secondary" mt={0.5}>
          {description}
        </Typography>
      )}

      {change !== undefined && (
        <Box display="flex" alignItems="center" mt={0.5}>
          {isPositive ? (
            <ArrowUpward sx={{ fontSize: 14, color: changeColor }} />
          ) : (
            <ArrowDownward sx={{ fontSize: 14, color: changeColor }} />
          )}
          <Typography variant="caption" sx={{ color: changeColor, ml: 0.5 }}>
            {Math.abs(change).toFixed(1)}% revenue change
          </Typography>
        </Box>
      )}
    </Card>
  );
};

export default RegionSalesDistribution;